//EVENTS BACKUP

import React, { useState, useEffect } from "react";
import axios from "axios";
import DynamicTable from "../Components/DynamicTable";
import { useUser } from "../UserContext";
import { Switch } from "@mui/material";
import EventDetails from "./EventDetails/EventDetails"; // Import the new EventDetails component
import { format } from "date-fns";
import Avatar from "../Components/Avatar";
import { fromUTC } from "../GeneralUtil/timezoneUtil";
import ProfileBA from "../Profiles/BA/ProfileBA";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

export interface Event {
  event_id: number;
  creator_user_id: number;
  booking_date: string;
  start_time: string;
  end_time: string;
  event: string;
  event_desc: string;
  status: string;
  pooling_end_time: string;
  selection_end_time: string;
  created_at: string;
  updated_at: string;
  autobooked: boolean | null;
  no_ba: boolean | null;
  products: any[];
  ambassadors: Ambassador[] | null;
  account_json: {
    zip: string;
    city: string;
    address: string;
    account: string;
    state_code: string;
    account_avatar_url: string;
  };
  selected_ba_json: {
    ba_user_id: number;
    first_name: string;
    last_name: string;
    avatar_url: string;
  } | null;
}

export interface Ambassador {
  user_id: number;
  first_name: string;
  last_name: string;
  avatar_url: string;
  avg_overall?: number;
  username: string;
}

interface EventResponse extends Event {} // This ensures EventResponse has all Event properties

interface EventsProps {
  profileView?: {
    type: "BA" | "Brand" | "Account";
    id: number;
  };
}

const Events: React.FC<EventsProps> = ({ profileView }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { currentUser, selectedRoleId } = useUser();
  const [view, setView] = useState<string>("");
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState("upcoming");
  const [initialAvailability, setInitialAvailability] = useState<{
    [key: number]: boolean;
  }>({});
  const [currentAvailability, setCurrentAvailability] = useState<{
    [key: number]: boolean;
  }>({});
  const [selectedBA, setSelectedBA] = useState<{
    id: number;
    username: string;
  } | null>(null);

  useEffect(() => {
    if (currentUser) {
      // Determine the view based on user role
      let userView = "default";
      switch (currentUser.role) {
        case "BA":
          userView = "Ambassador";
          break;
        case "Brand":
        case "Supplier":
          userView = "Brand";
          break;
        case "Account":
        case "Owner":
          userView = "Account";
          break;
        case "Agency":
          userView = "Agency";
          break;
      }
      setView(userView);
    }
  }, [currentUser]);

  useEffect(() => {
    if (view && currentUser) {
      fetchEvents();
    }
  }, [view, currentUser, selectedRoleId, profileView]);

  // Initialize availability only for Pooling events when user is a BA
  useEffect(() => {
    if (events.length > 0 && currentUser?.role === "BA") {
      // Initialize availability only for Pooling events
      const poolingEvents = events.filter(
        (event) => event.status === "Pooling"
      );

      const initial = poolingEvents.reduce((acc, event) => {
        acc[event.event_id] =
          event.ambassadors?.some(
            (amb) => amb.user_id === currentUser.user_id
          ) || false;
        return acc;
      }, {} as { [key: number]: boolean });

      setInitialAvailability(initial);
      setCurrentAvailability(initial);
    }
  }, [events, currentUser]);

  const fetchEvents = async () => {
    if (!view) return;

    const params = {
      creatorUserId: currentUser?.user_id,
      view: view,
      selectedRoleId: selectedRoleId,
      profileView: profileView?.type,
      profileId: profileView?.id,
    };

    try {
      const response = await axios.get<EventResponse[]>(
        `${import.meta.env.VITE_API_URL}/events/events`,
        { params }
      );
      setEvents(response.data);
    } catch (err) {
      const errorMessage =
        axios.isAxiosError(err) && err.response
          ? `Error: ${err.response.data.error}. ${err.response.data.details}`
          : `Failed to fetch events`;
      setError(errorMessage);
    }
  };

  const handleAvailabilityChange = (eventId: number, isAvailable: boolean) => {
    setCurrentAvailability((prev) => ({
      ...prev,
      [eventId]: isAvailable,
    }));
  };

  // Add periodic and visibility-based sync
  useEffect(() => {
    // Sync when tab loses focus
    const handleVisibilityChange = () => {
      if (document.hidden) {
        syncAvailabilityChanges();
      }
    };

    // Sync periodically (every 30 seconds)
    const intervalId = setInterval(() => {
      syncAvailabilityChanges();
    }, 30000);

    // Add visibility change listener
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      syncAvailabilityChanges();
    };
  }, [currentAvailability, initialAvailability]);

  // Keep the existing syncAvailabilityChanges function as a backup
  const syncAvailabilityChanges = async () => {
    const changes: Array<{ eventId: number; isAvailable: boolean }> = [];

    Object.entries(currentAvailability).forEach(([eventId, isAvailable]) => {
      const eventIdNum = parseInt(eventId);
      if (initialAvailability[eventIdNum] !== isAvailable) {
        changes.push({
          eventId: eventIdNum,
          isAvailable,
        });
      }
    });

    if (changes.length === 0) {
      return;
    }

    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}/events/events/update-availability`,
        {
          changes,
          userId: currentUser?.user_id,
        }
      );
    } catch (error) {
      console.error("Error syncing availability changes:", error);
    }
  };

  const renderTalentPool = (ambassadors: Ambassador[] | null, event: Event) => {
    // For Booked events, show only the selected ambassador
    if (event.status === "Booked" && event.selected_ba_json) {
      const { ba_user_id, first_name, last_name, avatar_url } =
        event.selected_ba_json;
      const username = `${first_name}-${last_name}`.toLowerCase();

      return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
          <div
            style={{
              cursor: "pointer",
              border: "2px solid #6B20F5",
              padding: 2,
              borderRadius: "50%",
              position: "relative",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedBA({
                id: ba_user_id,
                username: username,
              });
            }}
          >
            <Avatar
              src={avatar_url}
              alt={`${first_name} ${last_name}`}
              size={30}
              title={`${first_name} ${last_name}`}
            />
            <CheckCircleIcon
              sx={{
                position: "absolute",
                bottom: -4,
                right: -4,
                color: "#6B20F5",
                backgroundColor: "white",
                borderRadius: "50%",
                fontSize: 16,
              }}
            />
          </div>
        </div>
      );
    }

    // For Pooling events, show all available ambassadors
    if (!ambassadors || ambassadors.length === 0) {
      return null;
    }

    return (
      <div style={{ display: "flex", marginLeft: "10px" }}>
        {ambassadors.map((ambassador, index) => (
          <div
            key={ambassador.user_id}
            style={{
              cursor: "pointer",
              marginLeft: index > 0 ? "-10px" : "0", // Negative margin for overlap
              zIndex: ambassadors.length - index, // Higher z-index for earlier avatars
              position: "relative",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedBA({
                id: ambassador.user_id,
                username: ambassador.username,
              });
            }}
          >
            <Avatar
              src={ambassador.avatar_url}
              alt={`${ambassador.first_name} ${ambassador.last_name}`}
              size={30}
              title={`${ambassador.first_name} ${ambassador.last_name}`}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderColumns = () => {
    const baseColumns = [
      {
        key: "booking_date",
        header: "Date",
        render: (value: string) => new Date(value).toLocaleDateString(),
      },
      {
        key: "time",
        header: "Time",
        render: (_: any, rowData: Event) =>
          `${formatTime(rowData.start_time)} - ${formatTime(rowData.end_time)}`,
      },
      {
        key: "account",
        header: "Account",
        render: (_: any, rowData: Event) => (
          <div>
            <div>{rowData.account_json.account}</div>
            <div
              style={{
                fontSize: "0.85em",
                color: "#666",
                marginTop: "4px",
              }}
            >
              {`${rowData.account_json.address}, ${rowData.account_json.city}, ${rowData.account_json.state_code} ${rowData.account_json.zip}`}
            </div>
          </div>
        ),
      },
      { key: "event", header: "Event Name" },
      { key: "status", header: "Status" },
    ];

    switch (currentUser?.role) {
      case "BA":
        return [
          ...baseColumns,
          {
            key: "availability",
            header: "Availability",
            render: (_: any, rowData: Event) => {
              if (rowData.status !== "Pooling") return null;
              return (
                <div onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={currentAvailability[rowData.event_id] || false}
                    onChange={(e) =>
                      handleAvailabilityChange(
                        rowData.event_id,
                        e.target.checked
                      )
                    }
                    color="primary"
                  />
                </div>
              );
            },
          },
        ];
      case "Brand":
      case "Supplier":
        return [
          ...baseColumns,
          {
            key: "talent_pool",
            header: "Talent Pool",
            render: (_: any, rowData: Event) =>
              renderTalentPool(rowData.ambassadors, rowData),
          },
        ];
      case "Account":
      case "Owner":
        return [
          ...baseColumns,
          {
            key: "talent_pool",
            header: "Talent Pool",
            render: (_: any, rowData: Event) =>
              renderTalentPool(rowData.ambassadors, rowData),
          },
        ];
      case "Agency":
        return [
          ...baseColumns,
          {
            key: "talent_pool",
            header: "Talent Pool",
            render: (_: any, rowData: Event) =>
              renderTalentPool(rowData.ambassadors, rowData),
          },
        ];
      default:
        return baseColumns;
    }
  };

  const renderAmbassadorActions = (event: Event) => {
    if (!event || !event.event_id || event.status !== "Pooling") {
      return null;
    }

    return (
      <div onClick={(e) => e.stopPropagation()}>
        <Switch
          checked={currentAvailability[event.event_id] || false}
          onChange={(e) =>
            handleAvailabilityChange(event.event_id, e.target.checked)
          }
          color="primary"
          inputProps={{ "aria-label": "controlled" }}
        />
      </div>
    );
  };

  const formatTime = (time: string) => {
    if (!time) return "";
    const localTime = fromUTC(time);
    return format(new Date(`2000-01-01T${localTime}`), "h:mm aa");
  };

  const sections = [
    { label: "Upcoming Events", value: "upcoming" },
    { label: "Archive", value: "archive" },
  ];

  const handleSectionChange = (newSection: string) => {
    setCurrentSection(newSection);
  };

  const getFilteredEvents = () => {
    if (!events) return [];

    if (profileView) {
      // For profile views, only show Booked upcoming events or Completed past events
      return events.filter((event) => {
        const eventDate = new Date(event.booking_date);
        const today = new Date();

        if (currentSection === "upcoming") {
          return eventDate >= today && event.status === "Booked";
        } else {
          return eventDate < today && event.status === "Completed";
        }
      });
    }

    return events.filter((event) => {
      if (currentSection === "upcoming") {
        return ["Pooling", "Reviewing", "Booked"].includes(event.status);
      } else {
        // archive
        return ["Declined", "Completed"].includes(event.status);
      }
    });
  };

  // Add new function for event details columns
  const getEventDetails = () => {
    const baseDetails = [
      { key: "event", header: "Event Name" },
      {
        key: "products",
        header: "Products",
        render: (_: any, rowData: Event) => {
          if (!rowData.products || rowData.products.length === 0) {
            return "No products listed";
          }
          return rowData.products
            .map((product) => product.product_name)
            .join(", ");
        },
      },
      { key: "status", header: "Status" },
    ];

    switch (view) {
      case "Ambassador":
        // Only add availability column if event is in Pooling status
        return [
          ...baseDetails,
          ...(selectedEvent?.status === "Pooling"
            ? [
                {
                  key: "availability",
                  header: "Availability",
                  render: (_value: any, rowData: Event) =>
                    rowData ? renderAmbassadorActions(rowData) : null,
                },
              ]
            : []),
        ];
      case "agency":
        return [...baseDetails];
      default:
        return baseDetails;
    }
  };

  // Update sections for profile view
  const profileSections = [
    { label: "Upcoming Events", value: "upcoming" },
    { label: "Archive", value: "archive" },
  ];

  const handleRowClick = (row: Event) => {
    setSelectedEvent(row);
    setDetailsOpen(true);
  };

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div>
      <DynamicTable
        data={getFilteredEvents()}
        columns={renderColumns()}
        sections={profileView ? profileSections : sections}
        onSectionChange={handleSectionChange}
        onRowClick={handleRowClick}
      />
      {!profileView && (
        <EventDetails
          open={detailsOpen}
          events={events}
          columns={renderColumns()}
          eventDetails={getEventDetails()}
          onClose={() => setDetailsOpen(false)}
          eventData={selectedEvent}
          inert={detailsOpen}
        />
      )}
      {selectedBA && (
        <ProfileBA
          baId={selectedBA.id}
          username={selectedBA.username}
          isModal={true}
          isOpen={true}
          onClose={() => setSelectedBA(null)}
        />
      )}
    </div>
  );
};

export default Events;
